import axios from 'axios';
import env from '../../env';
import CryptoJS from 'crypto-js';
import queryString from 'query-string';
import { z } from 'zod';
import { createSecureChannel } from './securechannel';
import qs from 'qs';

export interface SendTxParams {
    network: string;
    from?: string;
    to?: string;
    gasLimit?: string;
    value?: string;
    data?: string;
    gasPrice?: string;
    nonce?: string;
    maxPriorityFeePerGas?: string;
    maxFeePerGas?: string;
}

export interface SignParams {
    network: string;
    encryptDevicePassword: string;
    pvencstr: string;
    uid: string;
    wid: string;
    sid: string;
    message?: string;
    to?: string;
    from?: string;
    value?: string;
    data?: string;
    nonce?: string;
    gasPrice?: string;
    gasLimit?: string;
    maxPriorityFeePerGas?: string;
    maxFeePerGas?: string;
    type: string; // PERSONAL, LEGACY, EIP1559
}

export interface DelegateRlpParams {
    signer: string;
    network: string;
    txType: string;
    from?: string;
    to?: string;
    feePayer?: string;
    gasLimit?: string;
    gasPrice?: string;
    value?: string;
    data?: string;
    nonce?: string;
}

export interface DelegateRawParams {
    network: string;
    txType: string;
    from?: string;
    to?: string;
    feePayer?: string;
    gasLimit?: string;
    gasPrice?: string;
    value?: string;
    data?: string;
    nonce?: string;
    feePayerSignatureHex: string;
    senderSignatureHex: string;
}

export interface SignTypedParams {
    network: string;
    encryptDevicePassword: string;
    pvencstr: string;
    uid: string;
    wid: string;
    sid: string;
    messageJson: string;
}

export interface HashSignParams {
    encryptDevicePassword: string;
    pvencstr: string;
    uid: string;
    wid: string;
    sid: string;
    hash: string;
}

export const TxResultResponse = z.object({
    result: z.string(),
});

export interface TxReceipt {
    blockHash: string;
    blockNumber: string;
    contractAddress: string;
    cumulativeGasUsed: string;
    effectiveGasPrice: string;
    from: string;
    gasUsed: string;
    logs: {
        address: string;
        blockHash: string;
        blockNumber: string;
        data: string;
        logIndex: string;
        removed: string;
        topics: string[];
        transactionHash: string;
        transactionIndex: string;
    };
    logsBloom: string;
    status: string;
    to: string;
    transactionHash: string;
    transactionIndex: string;
    type: string;
}

export const SignResultResponse = z.object({
    serializedTx: z.string(),
    rawTx: z.string(),
});

export const HashSignResultResponse = z.object({
    signstr: z.string(),
    iserr: z.boolean(),
    errmsg: z.string(),
});

export interface LegacyResult {
    result: string;
}

interface AbcLoginResult {
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    refreshToken: string;
}

interface SecureChannel {
    ChannelID: string;
    Encrypted: string;
    Message: string;
    PrivateKey: string;
    SecretKey: string;
    ServerPublicKey: string;
}

const API_URL = `${env.REACT_APP_API_SERVER}/api/waas`;
const wapiBaseURL = `https://${env.ABC_SERVER_ADDRESS}/wapi`;

export const encrypt = async (plain: string) => {
    const secureChannel: SecureChannel = JSON.parse(window.sessionStorage.getItem('secureChannel') || '{}');
    const secretKey = secureChannel.SecretKey;
    const key = secretKey.substring(0, 32);
    const iv = secretKey.substring(32);
    const encrypted = CryptoJS.AES.encrypt(plain, CryptoJS.enc.Hex.parse(key), {
        iv: CryptoJS.enc.Hex.parse(iv),
        padding: CryptoJS.pad.Pkcs7,
        mode: CryptoJS.mode.CBC,
    });
    // @ts-ignore
    return {
        encryptPlain: encrypted.toString(),
        secureChannel: secureChannel.ChannelID,
    };
};

export const getEncryptPlain = async (plain: string) => {
    const res = await encrypt(plain);
    return res.encryptPlain;
};

export const getGasData = async (network: string, type: string): Promise<any> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/gasData`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                token: abcAuth.accessToken,
                network,
                type,
            }),
        });
        if (res.status !== 200) {
            throw new Error();
        }

        let restData = res.data;
        // if (restData && restData.message && restData.message.includes('The token has expired.')) {
        //     const res = await refreshToken();
        //     const accessToken = res?.accessToken;
        //
        //     if (accessToken !== '' && accessToken !== null && accessToken !== undefined) {
        //         const res = await axios.request({
        //             url: `${API_URL}/gasData`,
        //             method: 'post',
        //             headers: {
        //                 'content-type': 'application/x-www-form-urlencoded',
        //             },
        //             data: queryString.stringify({
        //                 token: accessToken,
        //                 network,
        //                 type,
        //             }),
        //         });
        //         if (res.status !== 200) {
        //             throw new Error();
        //         }
        //
        //         restData = res.data;
        //     }
        // }
        return restData;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const estimate = async (param: SendTxParams, type: string = 'EIP1559'): Promise<string> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/estimate`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                ...param,
                token: abcAuth.accessToken,
                type,
            }),
        });
        if (res.status !== 200) {
            throw new Error();
        }
        if (res.data?.errorMessage) console.log('!! estimate error : ', res.data?.errorMessage);
        const resData = TxResultResponse.parse(res.data);
        return resData.result;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const sign = async (param: SignParams, secureChannel: string): Promise<string> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/sign`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                ...param,
                token: abcAuth.accessToken,
                secureChannel,
            }),
        });
        if (res.status !== 200) {
            throw new Error();
        }
        if (res.data.code === '752') {
            // TODO : Refresh Secure Channel
            await createSecureChannel();
            // alert(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
            console.log(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
            // TODO : Auto Logout
            // window.sessionStorage.clear();
            // window.localStorage.removeItem('login');
            // window.localStorage.removeItem('wallet');
            // window.localStorage.setItem('walletStatus', 'disconnected');
            // window.localStorage.removeItem('nftapiJwtToken');
            // window.location.href = '/';
            // alert('Token is expired. 다시 로그인 주세요.');
            throw new Error(res.data.message);
        }

        const resData = SignResultResponse.parse(res.data);
        return resData.serializedTx;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const delegateRlp = async (param: DelegateRlpParams): Promise<string> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/delegaterlp`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                ...param,
                token: abcAuth.accessToken,
            }),
        });
        if (res.status !== 200) {
            throw new Error();
        }

        console.log('## delegateRlp ', res.data?.result);
        return res.data?.result;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const delegateRaw = async (param: DelegateRawParams): Promise<string> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/delegateraw`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                ...param,
                token: abcAuth.accessToken,
            }),
        });
        if (res.status !== 200) {
            throw new Error();
        }

        console.log('## delegateRaw ', res.data?.result);
        // const resData = SignResultResponse.parse(res.data);
        return res.data?.result;
        // return resData.serializedTx;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const signTypedData = async (param: SignTypedParams, secureChannel: string): Promise<string> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const res = await axios.request({
            url: `${API_URL}/signtyped`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            data: queryString.stringify({
                ...param,
                token: abcAuth.accessToken,
                secureChannel,
            }),
        });
        console.log('!!!!! signTypedData : ', res.data);
        if (res.status !== 200) {
            throw new Error();
        }
        if (res.data.code === '752') {
            // TODO : Refresh Secure Channel
            await createSecureChannel();
            // alert(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
            console.log(`Secure Channel을 갱신했습니다. 다시 시도해 주세요.`);
            // TODO : Auto Logout
            // window.sessionStorage.clear();
            // window.localStorage.removeItem('login');
            // window.localStorage.removeItem('wallet');
            // window.localStorage.setItem('walletStatus', 'disconnected');
            // window.localStorage.removeItem('nftapiJwtToken');
            // window.location.href = '/';
            // alert('Token is expired. 다시 로그인 주세요.');
            throw new Error(res.data.message);
        }

        const resData = SignResultResponse.parse(res.data);
        return resData.serializedTx;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const getNetworkNonce = async (network: string, address: string): Promise<any> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');

        const networkStr = network === '0x2019' ? 'kaia' : 'kaia_kairos';
        const res = await axios.request({
            url: `${wapiBaseURL}/v2/transactions/count?address=${address}&network=${networkStr}&block=latest`,
            method: 'get',
            headers: {
                authorization: 'Bearer ' + abcAuth.accessToken,
            },
        });
        if (res.status !== 200) {
            throw new Error();
        }

        let resData = res.data;
        console.log('getNetworkNonce : ', resData);
        return resData.result;
    } catch (error: any) {
        throw error?.response?.data;
    }
};

export const signHash = async (txData: any): Promise<any> => {
    try {
        const abcAuth: AbcLoginResult = JSON.parse(window.sessionStorage.getItem('abcAuth') || '{}');
        const secureChannel: SecureChannel = JSON.parse(window.sessionStorage.getItem('secureChannel') || '{}');

        const res = await axios.request({
            url: `${wapiBaseURL}/v2/sign/hash`,
            method: 'post',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                authorization: `Bearer ${abcAuth.accessToken}`,
                'Secure-Channel': secureChannel.ChannelID,
            },
            data: qs.stringify(txData),
        });
        if (res.status !== 200) {
            throw new Error();
        }

        let resData = res.data;
        console.log('signHash : ', res);
        return resData.signstr;
    } catch (error: any) {
        throw error?.response?.data;
    }
};
